import { parse } from 'graphql';

import { createAuthenticatedGraphQLClient } from '../auth/createAuthenticatedGraphQLClient';
import { createClientGraphQLClient } from '../graphql/client/createClientGraphQLClient';
import type { RuntimeEnvironment } from '../graphql/config/runtimeConfig';
import type { RoutedGraphQLRequest } from '../graphql/routing/apiRouting';
import type {
  CreateCanvasserOnboardingTokenMutation,
  OnboardCanvasserMutation,
} from '../graphql/queries/canvasserOnboarding.generated';
import { classifyOnboardingError, type OnboardingError } from './onboardingErrors';
import { isNonBlankString, isRecord, parseFutureDateTime } from './onboardingValidation';

export type IssueCanvasserOnboardingTokenInput = {
  id: string;
};

export type ConsumeCanvasserOnboardingTokenInput = {
  token: string;
  name: string;
  phone: string;
  email: string;
  password: string;
};

export type IssuedCanvasserOnboardingToken = {
  token: string;
  expiresAt: Date;
};

export type OnboardedCanvasser = {
  authJwt: string;
  agentId: string;
  brokerageId: string;
  isBroker: boolean;
};

export type OnboardingResult<T> = { kind: 'success'; value: T } | OnboardingError;

export type OnboardingRequestOptions = {
  signal?: AbortSignal;
};

export type OnboardingGraphQLClient = {
  request<TData>(request: RoutedGraphQLRequest): Promise<TData>;
};

type OnboardingClientOptions = {
  env: RuntimeEnvironment;
  fetchImpl?: Parameters<typeof createClientGraphQLClient>[0]['fetchImpl'];
};

type OnboardingClientFactory = (options: OnboardingClientOptions) => OnboardingGraphQLClient;

export type CreateOnboardingApiOptions = OnboardingClientOptions & {
  now?: () => Date;
  authenticatedClientFactory?: OnboardingClientFactory;
  publicClientFactory?: OnboardingClientFactory;
};

export type OnboardingApi = {
  issueToken(
    input: IssueCanvasserOnboardingTokenInput,
    options?: OnboardingRequestOptions,
  ): Promise<OnboardingResult<IssuedCanvasserOnboardingToken>>;
  consumeToken(
    input: ConsumeCanvasserOnboardingTokenInput,
    options?: OnboardingRequestOptions,
  ): Promise<OnboardingResult<OnboardedCanvasser>>;
};

// Keep this selection set in sync with canvasserOnboarding.graphql. Codegen emits
// only types, so this is the runtime operation document used by the routed client.
export const CANVASSER_ONBOARDING_DOCUMENT = parse(/* GraphQL */ `
  mutation CreateCanvasserOnboardingToken($id: ID!) @api(name: core) {
    createCanvasserOnboardingToken(id: $id) {
      token
      expiresAt
    }
  }

  mutation OnboardCanvasser(
    $token: String!
    $name: String!
    $phone: String!
    $email: String!
    $password: String!
  ) @api(name: core) {
    onboardCanvasser(token: $token, name: $name, phone: $phone, email: $email, password: $password) {
      authJwt
      agentId
      brokerageId
      isBroker
    }
  }
`);

const retryableResult = <T>(): OnboardingResult<T> => ({ kind: 'retryable' });

const hasValidIssueInput = (input: unknown): input is IssueCanvasserOnboardingTokenInput =>
  isRecord(input) && isNonBlankString(input.id);

const hasValidConsumeInput = (input: unknown): input is ConsumeCanvasserOnboardingTokenInput =>
  isRecord(input) &&
  isNonBlankString(input.token) &&
  isNonBlankString(input.name) &&
  isNonBlankString(input.phone) &&
  isNonBlankString(input.email) &&
  isNonBlankString(input.password);

const readIssuedToken = (
  result: CreateCanvasserOnboardingTokenMutation,
  now: Date,
): IssuedCanvasserOnboardingToken | null => {
  const issuedToken = result.createCanvasserOnboardingToken;
  if (!issuedToken || !isNonBlankString(issuedToken.token)) return null;

  const expiresAt = parseFutureDateTime(issuedToken.expiresAt, now);
  if (!expiresAt) return null;

  return { token: issuedToken.token, expiresAt };
};

const readOnboardedCanvasser = (result: OnboardCanvasserMutation): OnboardedCanvasser | null => {
  const onboarding = result.onboardCanvasser;
  if (
    !onboarding ||
    !isNonBlankString(onboarding.authJwt) ||
    !isNonBlankString(onboarding.agentId) ||
    !isNonBlankString(onboarding.brokerageId) ||
    typeof onboarding.isBroker !== 'boolean'
  ) {
    return null;
  }

  return {
    authJwt: onboarding.authJwt,
    agentId: onboarding.agentId,
    brokerageId: onboarding.brokerageId,
    isBroker: onboarding.isBroker,
  };
};

// The public client is separately constructed without auth options. This prevents
// a stored broker session from ever being sent while a canvasser consumes a QR token.
export const createOnboardingApi = ({
  env,
  fetchImpl,
  now = () => new Date(),
  authenticatedClientFactory = createAuthenticatedGraphQLClient,
  publicClientFactory = createClientGraphQLClient,
}: CreateOnboardingApiOptions): OnboardingApi => {
  const authenticatedClient = authenticatedClientFactory({ env, fetchImpl });
  const publicClient = publicClientFactory({ env, fetchImpl });

  return {
    issueToken: async (input, options = {}) => {
      if (!hasValidIssueInput(input)) return { kind: 'invalid-request' };

      try {
        const result = await authenticatedClient.request<CreateCanvasserOnboardingTokenMutation>({
          document: CANVASSER_ONBOARDING_DOCUMENT,
          variables: { id: input.id },
          operationName: 'CreateCanvasserOnboardingToken',
          signal: options.signal,
        });
        const issuedToken = readIssuedToken(result, now());
        return issuedToken ? { kind: 'success', value: issuedToken } : retryableResult();
      } catch (error) {
        return classifyOnboardingError(error);
      }
    },
    consumeToken: async (input, options = {}) => {
      if (!hasValidConsumeInput(input)) return { kind: 'invalid-request' };

      try {
        const result = await publicClient.request<OnboardCanvasserMutation>({
          document: CANVASSER_ONBOARDING_DOCUMENT,
          variables: {
            token: input.token,
            name: input.name,
            phone: input.phone,
            email: input.email,
            password: input.password,
          },
          operationName: 'OnboardCanvasser',
          signal: options.signal,
        });
        const onboardedCanvasser = readOnboardedCanvasser(result);
        return onboardedCanvasser ? { kind: 'success', value: onboardedCanvasser } : retryableResult();
      } catch (error) {
        return classifyOnboardingError(error);
      }
    },
  };
};
